import React, { useState } from "react";
import { Modal, Box, Typography, LinearProgress, List, ListItem, ListItemText, Button, Divider, Collapse, Card, CardContent } from "@mui/material";
import CollapsibleCard, { CollapsibleCardHeader } from "components/shared/CollapsibleCard";
import { CheckCircle, FileUploadOutlined } from "@mui/icons-material";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  overflowY: "scroll",
  maxHeight: "calc(80vh)",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
};

export default function UploadProgressModal({ open, files, onClose, numberOfFilesToUpload }) {
  const [showDetails, setShowDetails] = useState(false);
  const allCompleted = files.every((file) => file.status === "COMPLETED") && files.length === numberOfFilesToUpload;
  const allFilesProgress =
    (files.reduce((sum, file) => {
      return sum + (file.progress || 0);
    }, 0) /
      (numberOfFilesToUpload * 100)) *
    100;

  const categorizedFiles = {
    acord: files.filter((f) => f.isAcord25),
    additional: files.filter((f) => !(f.isAcord25 || f.isSupplementalCoiFile)),
    supplemental: files.filter((f) => f.isSupplementalCoiFile),
  };

  const renderProgressItem = (file) => (
    <ListItemText
      primary={file.name}
      secondary={
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {file.status} {file.progress}%
          </Typography>
          <LinearProgress variant="determinate" value={file.progress} sx={{ mt: 1 }} />
        </Box>
      }
    />
  );

  return (
    <Modal open={open} aria-labelledby="upload-progress-modal" aria-describedby="modal-showing-file-upload-progress">
      <Box sx={style}>
        <Typography variant="h6" component="h2" mb={2}>
          Uploading Files
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1,}}>
            {allCompleted ? (
              <>
                <CheckCircle color="success" />
                <Typography>Upload Complete</Typography>
              </>
            ) : (
              <>
                <FileUploadOutlined />
                <Typography>Uploading...</Typography>
              </>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ ml: "auto !important" }}>
              {Math.round(allFilesProgress)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={allFilesProgress} sx={{ mt: 1 }} />
        </Box>
        <CollapsibleCard>
          <CollapsibleCardHeader
            sx={{
              padding: 0,
              alignItems: "center",
              ".MuiCardHeader-content .MuiButton-text": {
                padding: 0,
              },
            }}
            toggle={() => {
              setShowDetails(!showDetails);
            }}
            title={
              <>
                <Box component="span">{showDetails ? "Hide Details" : "Show Details"}</Box>
              </>
            }
          />
          <CardContent sx={{ padding: 0, mt: 2 }}>
            <Collapse in={showDetails}>
              <Card variant="outlined">
                <List>
                  {categorizedFiles.acord.length > 0 && (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Acord 25 Certificate
                      </Typography>
                      {categorizedFiles.acord.map((file, index) => (
                        <ListItem key={`acord-${index}`}>{renderProgressItem(file)}</ListItem>
                      ))}
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}

                  {categorizedFiles.additional.length > 0 && (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Optional Files
                      </Typography>
                      {categorizedFiles.additional.map((file, index) => (
                        <ListItem key={`additional-${index}`}>{renderProgressItem(file)}</ListItem>
                      ))}
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}

                  {categorizedFiles.supplemental.length > 0 && (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Supplemental COI Documentation
                      </Typography>
                      {Object.entries(
                        categorizedFiles.supplemental.reduce((acc, file) => {
                          if (!acc[file.insuranceType]) {
                            acc[file.insuranceType] = {};
                          }
                          if (!acc[file.insuranceType][file.documentType]) {
                            acc[file.insuranceType][file.documentType] = [];
                          }
                          acc[file.insuranceType][file.documentType].push(file);
                          return acc;
                        }, {})
                      ).map(([insuranceType, docTypes]) => (
                        <Box key={insuranceType} sx={{ ml: 2 }}>
                          <Typography variant="subtitle2">{insuranceType}</Typography>
                          {Object.entries(docTypes).map(([docType, files]) => (
                            <Box key={docType} sx={{ ml: 2 }}>
                              <Typography variant="body2">{docType}</Typography>
                              {files.map((file, index) => (
                                <ListItem key={`${insuranceType}-${docType}-${index}`}>{renderProgressItem(file)}</ListItem>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </>
                  )}
                </List>
              </Card>
            </Collapse>
          </CardContent>
        </CollapsibleCard>

        {allCompleted && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
